import api from './api'
import type { JournalEntryLine } from './accounting'

export interface PrefEquityDraftRequest {
  month_end_date: string
}

export interface PrefEquityLedgerRow {
  date: string
  prime_rate: number
  opening_balance: number
  issuance: number
  repayment: number
  daily_interest: number
  accrued_uncapitalized: number
  capitalized: boolean
  closing_balance: number
  instrument: string
  memo: string
}

export interface PrefEquityPrincipalEvent {
  date: string
  amount: number
  kind: 'conversion' | 'issuance'
  attribution: Record<string, number>
}

export interface PrefEquityOwnershipTransfer {
  effective_date: string
  deltas: Record<string, number>
  book_before: Record<string, number>
  targets?: Record<string, number>
  old_shares?: Record<string, number>
  new_shares?: Record<string, number>
}

export interface PrefEquitySourceData {
  month_end: string
  l_period_earned: number
  k_accrued_uncapitalized: number
  h_capitalized: number | null
  is_capitalization_month: boolean
  cap_date: string | null
  opening_balance: number
  closing_balance: number
  economic_balance: number
  treatment: string
  ledger_rows: PrefEquityLedgerRow[]
  pref_return_by_member?: Record<string, number>
  preferred_ownership_used?: Record<string, Record<string, number | string>>
  principal_swept_by_member?: Record<string, number>
  principal_events?: PrefEquityPrincipalEvent[]
  ownership_transfers?: PrefEquityOwnershipTransfer[]
  book_balance_by_member?: Record<string, number>
  cumulative_principal_by_member?: Record<string, number>
  cumulative_return_by_member?: Record<string, number>
}

export interface PrefEquityDraftResponse {
  id?: number | null
  lines: JournalEntryLine[]
  warnings: string[]
  private_note: string
  doc_number: string
  txn_date: string
  source_data: PrefEquitySourceData
  total_debits: number | string
  total_credits: number | string
  status?: string
}

export interface PrefEquityHistoryItem {
  id: number
  tool: string
  status: string
  txn_date: string
  doc_number: string
  lines: JournalEntryLine[]
  total_debits: number | string
  total_credits: number | string
  warnings?: string[] | null
  source_data?: PrefEquitySourceData | null
  private_note?: string | null
  qbo_entity_id?: string | null
  qbo_url?: string | null
  error_message?: string | null
  created_at: string
  posted_at?: string | null
}

export interface PrefEquityHistoryResponse {
  drafts: PrefEquityHistoryItem[]
  count: number
}

export async function createPrefEquityDraft(
  monthEndDate: string
): Promise<PrefEquityDraftResponse> {
  const response = await api.post<PrefEquityDraftResponse>(
    '/journal-entries/pref-equity/draft',
    { month_end_date: monthEndDate }
  )
  return response.data
}

export async function getPrefEquityHistory(): Promise<PrefEquityHistoryResponse> {
  const response = await api.get<PrefEquityHistoryResponse>(
    '/journal-entries/pref-equity/history'
  )
  return response.data
}

export async function deletePrefEquityHistoryItem(draftId: number): Promise<void> {
  await api.delete(`/journal-entries/pref-equity/history/${draftId}`)
}
